import { ArrowDropDown } from "@mui/icons-material";
import { Divider, Menu, MenuItem, Paper, Typography } from "@mui/material";
import { useTheme } from "@mui/styles";
import { useState } from "react";
import { selectUser } from "../../reducers/userSlice";
import { useSelector } from "react-redux";
import { selectRTFO } from "../../reducers/rtfoSlice";
import { rtfoConversionFactors } from "./rtfoConversionFactors";

export const RTFOMetricsPanel = ({ darkMode, currentStage, perfSchedule, settings }) => {
  const theme = useTheme();

  const user = useSelector(selectUser);
  const userEmail = user?.value?.email;

  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;

  const [metricsSlots, setMetricsSlots] = useState(user?.value?.rtfoMetricsSlots || defaultMetrics);

  const ENV = process.env.REACT_APP_NODE_ENV;
  const BASE_URL_SSD = ENV === 'production' ? process.env.REACT_APP_BASE_URL_SSD : (ENV === 'staging' || ENV === 'development' ) ? process.env.REACT_APP_BASE_URL_SSD_STAGING : 'http://localhost:8080/api';

  const units = currentRTFO?.settings?.units || 'metric';

  return (
    <Paper elevation={0} sx={{boxShadow: 1}} style={{
      margin: '10px 20px',
      padding: '0px 20px',
      height: 80,
      overflowX: 'auto',
      display: 'flex', 
      flexDirection: 'row', 
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
    }}>
      {currentStage ? 
        Object.keys(metricsSlots)?.map((slot,i) => {
          const metricName = metricsSlots[slot];
          const metricParam = getMetricParamName(metricName);
          const metricUnit = getMetricUnits(metricParam, units);
          let metricValue = getMetricValue(metricParam, currentStage, perfSchedule, settings);
          metricValue = convertMetricValue(metricParam, metricValue, units);
          return (
            <div style={{display: 'flex', flexDirection: 'column', textOverflow: 'ellipsis', overflow: 'hidden'}} key={metricName+"-"+i}>
              <BasicMenu metric={metricName} panelSlot={slot} metricsSlots={metricsSlots} setMetricsSlots={setMetricsSlots} email={userEmail} apiUrl={BASE_URL_SSD} />
              <div style={{display: 'flex', flexDirection: 'row', alignItems: 'flex-end'}}>  
                <Typography variant="h6">{metricValue}</Typography>
                <Typography variant="caption" style={{marginLeft: 5, marginBottom: 3}} noWrap>{!isNaN(metricValue) ? metricUnit : ''}</Typography>
              </div>
            </div>
          );
        })
      :
      <div style={{width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
        No stage selected
      </div>}
    </Paper>
  );
};

export function getMetricName(metric) {
  switch(metric) {
    case 'wellName':
      return 'Well';
    case 'stage':
      return 'Stage';
    case 'stageNumber':
      return 'Stage Number';
    case 'stageTime':
      return 'Stage Time';
    case 'stageSequence':
      return 'Stage Sequence';
    case 'numClusters':
      return '# Clusters';
    case 'clusterSpacing':
      return 'Cluster Spacing';
    case 'stageLength':
      return 'Stage Length';
    case 'averageDepth':
      return 'Average Depth';
    case 'tonsPerMin':
      return 'Propp. Placement Eff.';
    case 'fluidIntensity':
      return 'Fluid Intensity';
    case 'sandPerFluid':
      return 'Sand Per Fluid';
    case 'fluidPerSand':
      return 'Fluid Per Sand';
    case 'frPerSand':
      return 'DFR Per Sand';
    case 'frPerFluid':
      return 'DFR Per Fluid';
    case 'frPerSand2':
      return 'LFR Per Sand';
    case 'frPerFluid2':
      return 'LFR Per Fluid';
    case 'totalFr':
      return 'Total DFR';
    case 'totalFr2':
      return 'Total LFR';
    case 'preSandPressure':
      return 'Pre Sand Pressure';
    case 'preSandRate':
      return 'Pre Sand Rate';
    case 'preSandPNwb':
    case 'preSandPnwb':
      return 'Pre Sand Pnwb';
    case 'preSandPPerf':
    case 'preSandPperf':
      return 'Pre Sand Pperf';
    case 'pPerf':
      return 'Flush Pperf';
    case 'numShots':
      return 'Pre Sand Open Perfs';
    case 'N':
      return 'Flush Open Perfs';
    case 'perfEfficiency':
      return 'Perf Efficiency';
    case 'isip':
      return 'ISIP Surf.';
    case 'isipBH':
      return 'ISIP BH';
    case 'fracGradient':
      return 'Frac Gradient';
    case 'perfClosure':
      return 'Rate Drop Events';
    case 'plugSlip':
      return 'Plug Slip Events';
    case 'sandoff':
      return 'Sandoff Events';
    case 'isolationIssue':
      return 'Isolation Issue Events';
    case 'equipmentIssue':
      return 'Equipment Issue Events';
    case 'lostSuction':
      return 'Lost Suction Events';
    case 'other':
      return 'Other Events';
    case 'fracfeed':
      return 'Fracfeed Events';
    case 'ballSeating':
      return 'Ball Seating Events';
    case 'timeToPreSand':
      return 'Time to Pre Sand';
    case 'cleanVolumeToPreSand':
      return 'Clean Vol. to Pre Sand'; 
    case 'totalSlurryVolume':
      return 'Total Slurry Volume'; 
    case 'totalCleanVolume':
      return 'Total Clean Volume'; 
    case 'totalProducedVolume':
      return 'Total Produced Volume'; 
    case 'aSqrtK':
      return 'A*sqrt(k)'; 
    case 'fracArea':
      return 'Frac Area'; 
    case 'halfLength':
      return 'Half Length'; 
    case 'overflushVolume':
      return 'Overflush Vol.'; 
    case 'wellSwapTime':
      return 'Well Swap Time'; 
    case 'tpm':
      return 'Proppant Intensity Planned'; 
    case 'tpmCalc':
      return 'Proppant Intensity Actual'; 
    case 'totalProppant':
      return 'Total Proppant'; 
    case 'maxProppant':
      return 'Max Proppant'; 
    case 'stressShadow':
      return 'Stress Shadow'; 
    case 'averagePumpingPressure':
      return 'Average Pumping Pr.'; 
    case 'averagePumpingRate':
      return 'Average Pumping Rate'; 
    case 'breakdownPressure':
      return 'Breakdown Pressure'; 
    case 'topDepth':
      return 'Top Depth'; 
    case 'bottomDepth':
      return 'Btm. Depth'; 
    case 'localMagnitude':
      return 'Local Magnitude'; 
    case 'xf':
      return 'Xf'; 
    case 'vfr':
      return 'VFR'; 
    case 'percentEnergyLost':
      return 'Percent Energy Lost'; 
    case 'dPPerLogCycle':
      return 'dP Per Log Cycle'; 
    case 'pumpdownISIP':
      return 'Pumpdown ISIP'; 
    case 'postPerfISIP':
      return 'Post Perf ISIP'; 
    case 'deltaISIP':
      return 'Delta ISIP'; 
    default:
      return metric;
  };
};

export function getMetricParamName(param) {
  switch(param) {
    case 'Well':
      return 'wellName';
    case 'Stage':
      return 'stage';
    case 'Stage Number':
      return 'stageNumber';
    case 'Stage Sequence':
      return 'stageSequence';
    case '# Clusters':
      return 'numClusters';
    case 'Cluster Spacing':
      return 'clusterSpacing';
    case 'Stage Length':
      return 'stageLength';
    case 'Stage Time':
      return 'stageTime';
    case 'Average Depth':
      return 'averageDepth';
    case 'Propp. Placement Eff.':
      return 'tonsPerMin';
    case 'Fluid Intensity':
      return 'fluidIntensity';
    case 'Sand Per Fluid':
      return 'sandPerFluid';
    case 'Fluid Per Sand':
      return 'fluidPerSand';
    case 'DFR Per Sand':
      return 'frPerSand';
    case 'DFR Per Fluid':
      return 'frPerFluid';
    case 'LFR Per Sand':
      return 'frPerSand2';
    case 'LFR Per Fluid':
      return 'frPerFluid2';
    case 'Total DFR':
      return 'totalFr';
    case 'Total LFR':
      return 'totalFr2';
    case 'Pre Sand Pressure':
      return 'preSandPressure';
    case 'Pre Sand Rate':
      return 'preSandRate';
    case 'Pre Sand Pnwb':
      return 'preSandPNwb';
    case 'Pre Sand Pperf':
      return 'preSandPPerf';
    case 'Flush Pperf':
      return 'pPerf';
    case 'Pre Sand Open Perfs':
      return 'numShots';
    case 'Flush Open Perfs':
      return 'N';
    case 'Perf Efficiency':
      return 'perfEfficiency';
    case 'ISIP Surf.':
      return 'isip';
    case 'ISIP BH':
      return 'isipBH';
    case 'Frac Gradient':
      return 'fracGradient';
    case 'Rate Drop Events':
      return 'perfClosure';
    case 'Plug Slip Events':
      return 'plugSlip';
    case 'Sandoff Events':
      return 'sandoff';
    case 'Isolation Issue Events':
      return 'isolationIssue';
    case 'Equipment Issue Events':
      return 'equipmentIssue';
    case 'Lost Suction Events':
      return 'lostSuction';
    case 'Other Events':
      return 'other';
    case 'Fracfeed Events':
      return 'fracfeed';
    case 'Ball Seating Events':
      return 'ballSeating';
    case 'Time to Pre Sand':
      return 'timeToPreSand';
    case 'Total Slurry Volume':
      return 'totalSlurryVolume'; 
    case 'Total Clean Volume':
      return 'totalCleanVolume'; 
    case 'Total Produced Volume':
      return 'totalProducedVolume'; 
    case 'Clean Vol. to Pre Sand':
      return 'cleanVolumeToPreSand'; 
    case 'A*sqrt(k)':
      return 'aSqrtK'; 
    case 'Frac Area':
      return 'fracArea'; 
    case 'Half Length':
      return 'halfLength'; 
    case 'Overflush Vol.':
      return 'overflushVolume'; 
    case 'Well Swap Time':
      return 'wellSwapTime'; 
    case 'Proppant Intensity Planned':
      return 'tpm'; 
    case 'Proppant Intensity Actual':
      return 'tpmCalc'; 
    case 'Total Proppant':
      return 'totalProppant'; 
    case 'Max Proppant':
      return 'maxProppant'; 
    case 'Stress Shadow':
      return 'stressShadow'; 
    case 'Average Pumping Pr.':
      return 'averagePumpingPressure'; 
    case 'Average Pumping Rate':
      return 'averagePumpingRate'; 
    case 'Breakdown Pressure':
      return 'breakdownPressure'; 
    case 'Top Depth':
      return 'topDepth'; 
    case 'Btm. Depth':
      return 'bottomDepth'; 
    case 'Local Magnitude':
      return 'localMagnitude';
    case 'Xf':
      return 'xf'; 
    case 'VFR':
      return 'vfr'; 
    case 'Percent Energy Lost':
      return 'percentEnergyLost'; 
    case 'dP Per Log Cycle':
      return 'dPPerLogCycle';
    case 'Pumpdown ISIP':
      return 'pumpdownISIP';
    case 'Post Perf ISIP':
      return 'postPerfISIP';
    case 'Delta ISIP':
      return 'deltaISIP';
    default:
      return param;
  };
};

export function getMetricUnits(metric, units='metric') {
  switch(metric) {
    case 'stage':
    case 'stageNumber':
      return '';
    case 'stressShadow':
      return units === 'field' ? 'psi' : 'MPa';
    case 'stageTime':
      return 'min';
    case 'clusterSpacing':
    case 'stageLength':
    case 'halfLength':
    case 'topDepth':
    case 'bottomDepth':
    case 'averageDepth':
    case 'xf':
    case 'Depth Below Top Montney':
    case 'depth':
      return units === 'field' ? 'ft' : 'm';
    case 'tonsPerMin':
      return units === 'field' ? 'lb/min' : 'tons/min';
    case 'fluidIntensity':
      return units === 'field' ? 'bbl/ft' : 'm3/m';
    case 'sandPerFluid':
      return units === 'field' ? 'lb/bbl' : 'ton/m3';
    case 'fluidPerSand':
      return units === 'field' ? 'bbl/lb' : 'm3/ton';
    case 'frPerSand':
      return units === 'field' ? 'lb/lb' : 'kg/ton';
    case 'frPerFluid':
      return units === 'field' ? 'lb/bbl' : 'kg/m3';
    case 'frPerSand2':
      return units === 'field' ? 'gal/lb' : 'L/ton';
    case 'frPerFluid2':
      return units === 'field' ? 'gal/mgal' : 'L/m3';
    case 'totalFr':
      return units === 'field' ? 'lb' : 'kg';
    case 'totalFr2':
      return units === 'field' ? 'gal' : 'L';
    case 'preSandPressure':
    case 'preSandPNwb':
    case 'preSandPPerf':
    case 'pPerf':
    case 'isip':
    case 'isipBH':
    case 'averagePumpingPressure':
    case 'breakdownPressure':
    case 'pressure':
    case 'pumpdownISIP':
    case 'postPerfISIP':
    case 'deltaISIP':
      return units === 'field' ? 'psi' : 'MPa';
    case 'preSandRate':
    case 'rate':
      return units === 'field' ? 'bbl/min' : 'm3/min';
    case 'perfEfficiency':
      return '%';
    case 'fracGradient':
      return units === 'field' ? 'psi/ft' : 'kPa/m';
    case 'timeToPreSand':
      return 'min';
    case 'cleanVolumeToPreSand':
    case 'totalSlurryVolume':
    case 'totalCleanVolume':
    case 'totalProducedVolume':
    case 'overflushVolume':
    case 'vfr':
    case 'volume': 
      return units === 'field' ? 'bbl' : 'm3';
    case 'aSqrtK':
      return units === 'field' ? 'ft2*mD^0.5' : 'm2*mD^0.5'; 
    case 'fracArea':
      return units === 'field' ? 'ft2' : 'm2'; 
    case 'wellSwapTime':
      return 'min'; 
    case 'tpm':
    case 'tpmCalc':
    case 'tpmActual':
      return units === 'field' ? 'lb/ft' : 'ton/m'; 
    case 'totalProppant':
      return units === 'field' ? 'lb' : 'ton'; 
    case 'maxProppant':
      return units === 'field' ? 'lb/gal' : 'kg/m3';
    case 'averagePumpingRate':
      return units === 'field' ? 'bbl/min' : 'm3/min'; 
    case 'localMagnitude':
      return 'ML'; 
    case 'percentEnergyLost':
      return '%'; 
    case 'dPPerLogCycle':
      return units === 'field' ? 'psi / log cycle' : 'MPa / log cycle'; 
    default:
      return '';
  };
};

export function getMetricValue(metric, stage, perfSchedule, settings, monitorEvents=[], fiberData=[], stressShadow={}) {
  try {
    switch(metric) {
      case 'stage':
        return stage?.name?.split('_')?.[2];
      case 'wellName':
        return stage?.name?.split('_')?.[1];
      case 'numClusters':
        return +((stage?.metrics?.numClusters)?.toFixed(0)) || 0;
      case 'stageTime':
        return +((stage?.metrics?.stageTime)?.toFixed(1)) || 0;
      case 'tonsPerMin':
        return +((stage?.metrics?.tonsPerMin)?.toFixed(2)) || 0;
      case 'sandPerFluid':
        return +((stage?.metrics?.sandPerFluid)?.toFixed(2)) || 0;
      case 'frPerFluid':
        return +((stage?.metrics?.frPerFluid)?.toFixed(2)) || 0;
      case 'frPerSand':
        return +((stage?.metrics?.frPerSand)?.toFixed(2)) || 0;
      case 'frPerFluid2':
        return +((stage?.metrics?.frPerFluid2)?.toFixed(2)) || 0;
      case 'frPerSand2':
        return +((stage?.metrics?.frPerSand2)?.toFixed(2)) || 0;
      case 'totalFr':
        return +((stage?.metrics?.frPerFluid * stage?.metrics?.totalCleanVolume)?.toFixed(2)) || 0;
      case 'totalFr2':
        return +((stage?.metrics?.frPerFluid2 * stage?.metrics?.totalCleanVolume)?.toFixed(2)) || 0;
      case 'topDepth':
        try {
          const trimmedStageNumber = String(Number(String(stage?.stageNumber)?.replace(/[^0-9.]+|^0(?=[1-9])/g, '')?.trim()));
          const wellName = stage?.wellName?.toLowerCase();
          const stageClusters = perfSchedule?.wells?.[wellName]?.stages?.[trimmedStageNumber]?.clusters;
          const depths = stageClusters && Object.keys(stageClusters)?.map(cluster => stageClusters[cluster]?.shotDepth);    
          return +((Math.min(...depths))?.toFixed(1)) || 0;
        } catch (error) {
          return 0;
        };
      case 'bottomDepth':
        try {
          const trimmedStageNumber = String(Number(String(stage?.stageNumber)?.replace(/[^0-9.]+|^0(?=[1-9])/g, '')?.trim()));
          const wellName = stage?.wellName?.toLowerCase();
          const stageClusters = perfSchedule?.wells?.[wellName]?.stages?.[trimmedStageNumber]?.clusters;
          const depths = stageClusters && Object.keys(stageClusters)?.map(cluster => stageClusters[cluster]?.shotDepth);    
          return +((Math.max(...depths))?.toFixed(1)) || 0;
        } catch (error) {
          return 0;
        };
      case 'aSqrtK':
        return +((stage?.fracModel?.aSqrtK)?.toFixed(2)) || 0;
      case 'fracArea':
        return +((stage?.fracModel?.fracArea)?.toFixed(2)) || 0;
      case 'halfLength':
        return +((stage?.fracModel?.halfLength || (stage?.fracModel?.fracArea / (settings?.fracHeight * 2)))?.toFixed(2)) || 0;
      case 'perfClosure':
        return +((stage?.events?.perfClosure)?.length?.toFixed(0)) || 0;
      case 'plugSlip':
        return +((stage?.events?.plugSlip || stage?.plugSlips)?.length?.toFixed(0)) || 0;
      case 'sandoff':
        return +(stage?.events?.sandoff?.length?.toFixed(0)) || 0;
      case 'isolationIssue':
        return (+(stage?.events?.isolationIssue?.length?.toFixed(0)) || 0) + (stage?.pumpdownParams?.postPerfISIP - stage?.pumpdownParams?.pumpdownISIP < 1 ? 1 : 0);
      case 'equipmentIssue':
        return +(stage?.events?.equipmentIssue?.length?.toFixed(0)) || 0;
      case 'lostSuction':
        return +(stage?.events?.lostSuction?.length?.toFixed(0)) || 0;
      case 'other':
        return +(stage?.events?.other?.length?.toFixed(0)) || 0;
      case 'fracfeed':
        return +(stage?.events?.fracfeed?.length?.toFixed(0)) || 0;
      case 'ballSeating':
        return +(stage?.events?.ballSeating?.length?.toFixed(0)) || 0;
      case 'totalProppant':
        return +((stage?.metrics?.tpmCalc * stage?.metrics?.stageLength)?.toFixed(2)) || 0;
      case 'maxProppant':
        return +((stage?.metrics?.maxProppant)?.toFixed(2)) || 0;
      case 'tpm': {
        const stageName = stage?.name;
        const wellName = stageName?.split('_')?.[1]?.toLowerCase();
        return +(perfSchedule?.wells?.[wellName]?.stages?.[stageName?.split('_')?.[2]?.replace(/[^0-9]+/g, '')]?.tpm) || 0;
      };
      case 'stressShadow':
        return +(stressShadow?.[stage?.name]?.toFixed(2)) || 0;
      case 'perfEfficiency':
        return +(stage?.metrics?.perfEfficiency?.toFixed(1)) || 0;
      case 'xf': {
        let stageEvents = [];
        stageEvents = stageEvents.concat(monitorEvents?.filter(e => e && e.stageName && e.stageName === stage?.name && e.name !== 'Stage Start') || []);
        stageEvents = stageEvents.concat(fiberData?.filter(e => e && e.stageName && e.stageName === stage?.name && e.name !== 'Stage Start') || []);
        const totalSlurryVolume = stage?.metrics?.totalSlurryVolume || 0;

        const xfs = stageEvents?.map(e => {
          const calculatedXf = Math.sqrt(4 * Math.PI * totalSlurryVolume * e.diffusivity);
          const xf = calculatedXf || 0;
          return xf;
        });

        const averageXf = (xfs?.reduce((tot, curr) => tot + curr, 0) / xfs?.length) || 0;
        return +(averageXf?.toFixed(1)) || 0;
      };
      case 'vfr': {
        let stageEvents = [];
        stageEvents = stageEvents.concat(monitorEvents?.filter(e => e.stageName === stage?.name && e.name !== 'Stage Start'));
        stageEvents = stageEvents.concat(fiberData?.filter(e => e.stageName === stage?.name && e.name !== 'Stage Start'));
        const vfrs = stageEvents?.map(e => e.vfr || e.slurryVolume);
        const averageVFR = (vfrs?.reduce((tot, curr) => tot + curr, 0) / vfrs?.length) || 0;
        return +(averageVFR?.toFixed(1)) || 0;
      };
      case 'percentEnergyLost':
        return +((stage?.fracEnergy?.percentEnergyLost * 100)?.toFixed(2)) || 0;
      case 'dPPerLogCycle':
        return +((stage?.fracModel?.dPPerLogCycle)?.toFixed(3)) || 0;
      case 'isip':
        return +(stage?.metrics?.isip) || 0;
      case 'pumpdownISIP':
        return +(stage?.pumpdownParams?.pumpdownISIP) || 0;
      case 'postPerfISIP':
        return +(stage?.pumpdownParams?.postPerfISIP) || 0;
      case 'deltaISIP':
        return +(stage?.pumpdownParams?.postPerfISIP - stage?.pumpdownParams?.pumpdownISIP) || 0;
      default:
        return +(stage?.metrics?.[metric]?.toFixed(1)) || 0;
    };
  } catch (error) {
    console.log(error);
    return 0;
  };
};

export function convertMetricValue(metric, value, units='metric') {
  try {
    switch(metric) {
      case 'pressure':
      case 'isip':
      case 'isipBH':
      case 'falloffISIP':
      case 'stressShadow':
      case 'breakdownPressure':
      case 'averagePumpingPressure':
      case 'preSandPressure':
      case 'preSandPnwb':
      case 'preSandPNwb':
      case 'preSandPperf':
      case 'preSandPPerf':
      case 'pPerf':
      case 'pumpdownISIP':
      case 'postPerfISIP':
      case 'deltaISIP':
        return (value * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(2);
      case 'rate':
      case 'averagePumpingRate':
      case 'preSandRate':
        return (value * (units === 'field' ? rtfoConversionFactors.rate : 1))?.toFixed(2);
      case 'volume':
      case 'totalSlurryVolume':
      case 'totalCleanVolume':
      case 'overflushVolume':
      case 'cleanVolumeToPreSand':
      case 'dailyCleanVolume':
        return (value * (units === 'field' ? rtfoConversionFactors.slurryVolume : 1))?.toFixed(2);
      case 'tonsPerMin':
        return (value * (units === 'field' ? rtfoConversionFactors.tonsPerMin : 1))?.toFixed(2);
      case 'fluidIntensity':
        return (value * (units === 'field' ? rtfoConversionFactors.fluidIntensity : 1))?.toFixed(2);
      case 'sandPerFluid':
        return (value * (units === 'field' ? rtfoConversionFactors.sandPerFluid : 1))?.toFixed(2);
      case 'fluidPerSand':
        return (value * (units === 'field' ? rtfoConversionFactors.fluidPerSand : 1))?.toFixed(2);
      case 'frPerFluid':
        return (value * (units === 'field' ? rtfoConversionFactors.frPerFluid : 1))?.toFixed(2);
      case 'frPerSand':
        return (value * (units === 'field' ? rtfoConversionFactors.frPerSand : 1))?.toFixed(units === 'field' ? 6 : 2);
      case 'frPerFluid2':
        return (value * (units === 'field' ? rtfoConversionFactors.frPerFluid2 : 1))?.toFixed(2);
      case 'frPerSand2':
        return (value * (units === 'field' ? rtfoConversionFactors.frPerSand2 : 1))?.toFixed(units === 'field' ? 6 : 2);
      case 'totalFr':
        return (value * (units === 'field' ? rtfoConversionFactors.totalFr : 1))?.toFixed(2);
      case 'totalFr2':
        return (value * (units === 'field' ? rtfoConversionFactors.totalFr2 : 1))?.toFixed(2);
      case 'topDepth':
      case 'bottomDepth':
      case 'averageDepth':
      case 'depth':
      case 'halfLength':
      case 'xf':
      case 'stageLength':
      case 'clusterSpacing':
      case 'clusterTop':
      case 'dailyTotalLength':
        return (value * (units === 'field' ? rtfoConversionFactors.depth : 1))?.toFixed(1);
      case 'aSqrtK':
        return (value * (units === 'field' ? rtfoConversionFactors.fracArea : 1))?.toFixed(1);
      case 'fracArea':
        return (value * (units === 'field' ? rtfoConversionFactors.fracArea : 1))?.toFixed(1);
      case 'totalProppant':
        return (value * (units === 'field' ? rtfoConversionFactors.totalProppant : 1))?.toFixed(1);
      case 'maxProppant':
        return (value * (units === 'field' ? rtfoConversionFactors.proppant : 1))?.toFixed(1);
      case 'tpm': 
      case 'tpmActual': 
      case 'tpmPlanned': 
      case 'tpmCalc': 
        return (value * (units === 'field' ? rtfoConversionFactors.tpm : 1))?.toFixed(1);
      case 'vfr': 
        return (value * (units === 'field' ? rtfoConversionFactors.vfr : 1))?.toFixed(1);;
      case 'dPPerLogCycle':
        return (value * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(1);
      case 'fracGradient':
        return (value * (units === 'field' ? rtfoConversionFactors.fracGradient / 1000 : 1))?.toFixed(3);
      case 'dailyCleanVolumePerLength':
        return (value * (units === 'field' ? rtfoConversionFactors.dailyCleanVolumePerLength : 1))?.toFixed(1);
      default:
        return value;
    };
  } catch (error) {
    console.log(error);
    return 0;
  };
};

export const allMetrics = {
  'Stage Information': [
    'Well',
    'Stage',
    'Stage Time',
    '# Clusters',
    'Cluster Spacing',
    'Stage Length',
    'Top Depth',
    'Btm. Depth'
  ],
  'Pumping Information': [
    'Time to Pre Sand',
    'Propp. Placement Eff.',
    'Fluid Intensity',
    'Sand Per Fluid',
    'Fluid Per Sand',
    'DFR Per Sand',
    'DFR Per Fluid',
    'LFR Per Sand',
    'LFR Per Fluid',
    'Total DFR',
    'Total LFR',
    'Clean Vol. to Pre Sand',
    'Total Slurry Volume',
    'Total Clean Volume', 
    'Total Produced Volume', 
    'Overflush Vol.',
    'Proppant Intensity Planned',
    'Proppant Intensity Actual',
    'Total Proppant',
    'Average Pumping Pr.',
    'Average Pumping Rate'
  ],
  'ISIP': [
    'Breakdown Pressure',
    'ISIP Surf.',
    'ISIP BH',
    'Frac Gradient'
  ],
  'Stepdown Model': [
    'Pre Sand Pressure',
    'Pre Sand Rate',
    'Pre Sand Pnwb',
    'Pre Sand Pperf',
    'Flush Pperf',
    'Pre Sand Open Perfs',
    'Flush Open Perfs',
    'Perf Efficiency',
    'Rate Drop Events',
    'Plug Slip Events',
    'Sandoff Events',
    'Isolation Issue Events',
    'Equipment Issue Events',
    'Lost Suction Events',
    'Other Events',
    'Fracfeed Events'
  ],
  'Frac Model': [
    'A*sqrt(k)',
    'Frac Area',
    'Half Length',
    'Percent Energy Lost',
    'dP Per Log Cycle'
  ],
  'FDI': [
    'Xf',
    'VFR',
  ],
};

const defaultMetrics = {
  slot1: 'Well',
  slot2: 'Stage',
  slot3: '# Clusters',
  slot4: 'Top Depth',
  slot5: 'Btm. Depth',
  slot6: 'Stage Time',
  slot7: 'Propp. Placement Eff.',
  slot8: 'Fluid Intensity',
  slot9: 'Sand Per Fluid',
  slot10: 'DFR Per Fluid',
  slot11: 'DFR Per Sand',
  slot12: 'Pre Sand Pressure',
  slot13: 'Pre Sand Rate',
  slot14: 'Pre Sand Pnwb',
  slot15: 'Pre Sand Pperf',
  slot16: 'ISIP Surf.'
};

function BasicMenu({ metric, panelSlot, metricsSlots, setMetricsSlots, email, apiUrl }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleChangeMetric = async (metric, metricsSlots, setMetricsSlots, email, apiUrl) => {
    const newMetricsSlots = {...metricsSlots, [panelSlot]: metric};
    setMetricsSlots(newMetricsSlots);

    console.log(`Updating RTFO metrics slots for user ${email}`);

    await fetch(`${apiUrl}/users/rtfometricsslots`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': process.env.REACT_APP_API_KEY
      },
      body: JSON.stringify({
        email: email,
        rtfometricsslots: newMetricsSlots
      })
    });

    handleClose();
  };

  return (
    <div>
      <div 
        style={{display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'pointer'}}
        onClick={handleClick}
        title='Change Metric'
      >
        <Typography
          style={{fontSize: '12px'}}
        >
          {metric}
        </Typography>
        <ArrowDropDown style={{fontSize: '14px'}} />
      </div>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {Object.keys(allMetrics).map(group => {
          return (
            <div key={group}>
              <Typography variant="caption" style={{marginLeft: 10}}>{group}</Typography>
              {allMetrics[group]?.map(m => {
                return <MenuItem selected={m === metricsSlots[panelSlot]} onClick={() => handleChangeMetric(m, metricsSlots, setMetricsSlots, email, apiUrl)} key={m}>{m}</MenuItem>
              })}
              <Divider />
            </div>
          )
        })}
      </Menu>
    </div>
  );
};
